import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RequestStatus } from 'types'
import { PartialUpdateUserRequest } from 'types/swagger'
import api from 'utils/api'
import { RootState } from '../../store'
import { updateCurrentUser } from '../auth/authSlice'
import { showErrorNotification } from '../notifications/notificationsSlice'

interface EditProfile extends PartialUpdateUserRequest {
    id: number
}

export const saveProfileBadge = createAsyncThunk(
    'SAVE_PROFILE',
    async (props: EditProfile, thunkApi) => {
        thunkApi.dispatch(preventHideProfile(false))
        thunkApi.dispatch(editProfile(props))
        thunkApi.dispatch(toggleProfile())
    },
)

export const editProfile = createAsyncThunk(
    'EDIT_PROFILE',
    async ({ id, ...args }: EditProfile, thunkApi) => {
        const url = `/profiles/${id}/`
        const response = await api.safePatch<PartialUpdateUserRequest>(url, args)
        if (response.error) {
            thunkApi.dispatch(showErrorNotification(response.error))
            return thunkApi.rejectWithValue(response)
        }
        thunkApi.dispatch(updateCurrentUser(response.data))
        return response
    },
)

export const uploadProfileImage = createAsyncThunk(
    'UPLOAD_PROFILE_IMAGE',
    async (args: { id: number, body: FormData }, thunkApi) => {
        const url = `/profiles/${args.id}/mugshot/`
        const response = await api.safePatch(url, args.body)
        if (response.error) {
            thunkApi.dispatch(showErrorNotification(response.error))
            thunkApi.rejectWithValue(response)
        }
        thunkApi.dispatch(updateCurrentUser(response?.data))
        return response
    },
)

const editProfileSlice = createSlice({
    name: 'profile',
    initialState: {
        isShow: false as boolean,
        preventHideProfile: false as boolean,
        editProfileStatus: 'idle' as RequestStatus,
        profileImage: {
            data: null as { mugshot: string },
            status: 'idle' as RequestStatus,
        },
    },
    reducers: {
        toggleProfile: (state) => {
            state.isShow = state.preventHideProfile || !state.isShow
        },
        activateProfile: (state) => {
            state.preventHideProfile = true
            state.isShow = true
        },
        preventHideProfile: (state, { payload }: PayloadAction<boolean>) => {
            state.preventHideProfile = payload
        },
    },
    extraReducers: (builder) => {
        builder.addCase(uploadProfileImage.pending, (state) => {
            state.profileImage.status = 'loading'
        })
        builder.addCase(uploadProfileImage.fulfilled, (state, { payload }) => {
            state.profileImage.status = 'succeeded'
            state.profileImage.data = payload.data
        })
        builder.addCase(editProfile.pending, (state) => {
            state.editProfileStatus = 'loading'
        })
        builder.addCase(editProfile.fulfilled, (state) => {
            state.editProfileStatus = 'succeeded'
        })
        builder.addCase(editProfile.rejected, (state) => {
            state.editProfileStatus = 'failed'
        })
    },
})

export const toggleProfile = editProfileSlice.actions.toggleProfile
export const preventHideProfile = editProfileSlice.actions.preventHideProfile
export const activateProfile = editProfileSlice.actions.activateProfile

export const editProfileStatusSelector = (state: RootState) => state.editProfile.editProfileStatus

export default editProfileSlice.reducer
